import { ENUM_DATE_TIME_RANGE } from '@utils/library/dateTime/enums';
import { DateTimeDurationUnit, IUtilsDateTimeRange } from '@utils/library/dateTime/interfaces';
import { DateType } from 'global.interfaces';
import moment from 'moment-timezone';
import { Moment } from 'moment/moment';

export const DefaultDateFormat = 'YYYY-MM-DD HH:mm:ss';
export const DefaultDateFormatISO = 'YYYY-MM-DDTHH:mm:ss';
export const ShortDateFormat = 'YYYY-MM-DD';
export const DateTimeFormat = 'DD-MM-YYYY HH:mm';
export const DateTimeWithSecondFormat = 'DD-MM-YYYY HH:mm:ss';
export const TimeWithSecondFormat = 'HH:mm:ss';
const MINUTES_IN_HOUR = 60;

export const getUtcTime = () => moment.utc().toISOString();

export const getCurrentDayUtc = (): IUtilsDateTimeRange => {
  const dateTimeUtc = getUtcTime();

  return getCurrentDayUtcGivenDateTime(dateTimeUtc);
};

export const getFullMonthAndYear = (myDate: Date | string) => {
  return moment(myDate).format('MMMM YYYY');
};

export const getFullDateAndTime = (myDate: Date | string, options?: { useUtc: boolean }) => {
  return options?.useUtc
    ? moment.utc(myDate).format(DateTimeWithSecondFormat)
    : moment(myDate).format(DateTimeWithSecondFormat);
};

export const getCurrentDayUtcGivenDateTime = (dateTime: string): IUtilsDateTimeRange => {
  const dateTimeMoment = convertStringDateToMomentUtc(dateTime);

  return {
    from: dateTimeMoment.startOf('d').format(DefaultDateFormat),
    to: dateTimeMoment.endOf('d').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.currentDay,
  };
};

export const getPastDayUtc = (): IUtilsDateTimeRange => {
  const dateTimeUtc = getUtcTime();

  return getPastDayUtcGivenDateTime(dateTimeUtc);
};

export const getPastDayUtcGivenDateTime = (dateTime: string): IUtilsDateTimeRange => {
  const dateTimeMoment = convertStringDateToMomentUtc(dateTime);
  const previousDay = dateTimeMoment.subtract(1, 'day');

  return {
    from: previousDay.startOf('d').format(DefaultDateFormat),
    to: previousDay.endOf('d').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.pastDay,
  };
};

export const getCurrentMonthUtc = (): IUtilsDateTimeRange => {
  const dateTimeUtc = getUtcTime();

  return getCurrentMonthUtcGivenDateTime(dateTimeUtc);
};

export const getCurrentMonthUtcGivenDateTime = (dateTime: string): IUtilsDateTimeRange => {
  const dateTimeMoment = convertStringDateToMomentUtc(dateTime);

  return {
    from: dateTimeMoment.startOf('M').format(DefaultDateFormat),
    to: dateTimeMoment.endOf('M').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.currentMonth,
  };
};

export const getPastMonthUtc = (): IUtilsDateTimeRange => {
  const dateTimeUtc = getUtcTime();

  return getPastMonthUtcGivenDateTime(dateTimeUtc);
};

export const getPastMonthUtcGivenDateTime = (dateTime: string): IUtilsDateTimeRange => {
  const dateTimeMoment = convertStringDateToMomentUtc(dateTime);
  const monthAgo = dateTimeMoment.subtract(1, 'M');

  return {
    from: monthAgo.startOf('M').format(DefaultDateFormat),
    to: monthAgo.endOf('M').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.pastMonth,
  };
};

export const convertStringDateToMomentUtc = (dateTime: string) => moment.utc(dateTime);

export const getAll = (): IUtilsDateTimeRange => ({
  from: '',
  to: '',
  range: ENUM_DATE_TIME_RANGE.all,
});

export const getDateTimeWithOffset = (utcDateTime: string, offset: number, unit: DateTimeDurationUnit): Moment => {
  return moment.utc(utcDateTime).add(offset, unit);
};

export const getFormattedServerDateTime = (clientDateTime: DateType, offsetHours: number): string => {
  return moment.utc(clientDateTime).add(-offsetHours, 'hours').format(DefaultDateFormat);
};

export const getServerDateTime = (clientTime: DateType) => {
  return moment.utc(clientTime).format(DefaultDateFormat);
};

export const getUtcOffsetDescription = (offsetHours: number): string => {
  if (offsetHours === 0) return 'UTC';

  const nonBreakingSpaceForReact = '\u00A0';

  if (offsetHours > 0) return `+${offsetHours}${nonBreakingSpaceForReact}UTC`;

  return `${offsetHours}${nonBreakingSpaceForReact}UTC`;
};

export const getDefaultServerDateTime = (utcDateTime: DateType): string => {
  return moment(utcDateTime).format(DefaultDateFormat);
};

export const getFormattedClientDateTime = (utcDateTime: string, offsetHours: number, format: string): string => {
  return getDateTimeWithOffset(utcDateTime, offsetHours, 'hours').format(format);
};

export const isValidBirthDay = (currentDay: moment.MomentInput, minAllowedAge: number): boolean => {
  const maxDay = moment().subtract(minAllowedAge, 'year').endOf('D');

  return moment(currentDay).isBefore(maxDay);
};

export const isAfterDateTime = (currentDateTime: string, afterDateTime: DateType): boolean => {
  return moment(currentDateTime).isAfter(afterDateTime);
};

export const toClientDateTime = (utcDateTime: string, offsetHours: number) => {
  return moment.utc(utcDateTime).add(offsetHours, 'hours');
};

export const getClientTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getCurrentPlatformDateTime = (offsetHours: number, currentTimeUTC: Moment = moment.utc()) => {
  const dateTimeUtc = currentTimeUTC.clone();

  return dateTimeUtc.add(offsetHours, 'hours');
};

export const getCurrentPlatformDayRangeGivenOffset = (
  offsetHours: number,
  currentTimeUTC: Moment = moment.utc(),
): IUtilsDateTimeRange => {
  const dateTimeUtc = currentTimeUTC.clone();
  const currentDateTimePlatform = dateTimeUtc.add(offsetHours, 'hours');

  return {
    from: currentDateTimePlatform.startOf('d').format(DefaultDateFormat),
    to: currentDateTimePlatform.endOf('d').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.currentDay,
  };
};

export const getPastPlatformDayRangeGivenOffset = (
  offsetHours: number,
  currentTimeUTC: Moment = moment.utc(),
): IUtilsDateTimeRange => {
  const dateTimeUtc = currentTimeUTC.clone();

  const currentDateTimePlatform = dateTimeUtc.add(offsetHours, 'hours');
  const previousPlatformDay = currentDateTimePlatform.subtract(1, 'day');

  return {
    from: previousPlatformDay.startOf('d').format(DefaultDateFormat),
    to: previousPlatformDay.endOf('d').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.pastDay,
  };
};

export const getCurrentPlatformMonthRangeGivenOffset = (
  offsetHours: number,
  currentTimeUTC: Moment = moment.utc(),
): IUtilsDateTimeRange => {
  const dateTimeUtc = currentTimeUTC.clone();
  const currentDateTimePlatform = dateTimeUtc.add(offsetHours, 'hours');

  return {
    from: currentDateTimePlatform.startOf('M').format(DefaultDateFormat),
    to: currentDateTimePlatform.endOf('M').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.currentMonth,
  };
};

export const getPastPlatformMonthRangeGivenOffset = (
  offsetHours: number,
  currentTimeUTC: Moment = moment.utc(),
): IUtilsDateTimeRange => {
  const dateTimeUtc = currentTimeUTC.clone();
  const currentDateTimePlatform = dateTimeUtc.add(offsetHours, 'hours');
  const monthAgo = currentDateTimePlatform.subtract(1, 'M');

  return {
    from: monthAgo.startOf('M').format(DefaultDateFormat),
    to: monthAgo.endOf('M').format(DefaultDateFormat),
    range: ENUM_DATE_TIME_RANGE.pastMonth,
  };
};

export const getCurrentPlatformTimeZone = (offsetHours: number) => {
  const timeZones = moment.tz.names();
  const dataPlatform = getCurrentPlatformDateTime(offsetHours);
  const offsetInMinutes = offsetHours * MINUTES_IN_HOUR;

  const currentTimeZones = timeZones.filter((zone) => {
    const zoneOffset = moment.tz(dataPlatform, zone).utcOffset();

    return zoneOffset === offsetInMinutes;
  });

  return currentTimeZones.sort()?.[0];
};

export const getDuration = (startDate: string, endDate: string) => {
  const start = convertStringDateToMomentUtc(startDate);
  const end = convertStringDateToMomentUtc(endDate);
  const diff = end.diff(start);

  return moment.duration(diff);
};
